import React from 'react';
import { injectIntl, FormattedHTMLMessage } from 'gatsby-plugin-intl';
import styled from 'styled-components';
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import { device } from "./device";

import H3 from './styledComponents/H3';
import H4 from './styledComponents/H4';
import H5 from './styledComponents/H5';
import Paragraph from './styledComponents/Paragraph';
import RuleDescription from './styledComponents/RuleDescription';
import ChapterSpace from './styledComponents/ChapterSpace';

import { Container,Cell,Grid,ParentAnchor,ChapterHeader,AnchorBlock } from './blockComponents'
import { PlainImage } from './imageComponents'
import { ImageMultilang } from './utility/imageMultilang';

const AnchorLink = styled.span`
    cursor: pointer;
    font-weight: 500;
    text-decoration: underline;
    &:hover {
        color: currentColor;
        text-decoration: underline;
    }
`;
const UnderlineLink = styled.a`
    text-decoration: underline;
    &:hover {
        color: currentColor;
        text-decoration: underline;
    }
`;
const ConstructorImg = styled.div`
    font-size: 0;
    max-width: 654px;
    width: 100%;
`;
const SetImg = styled.div`
    margin-bottom: 48px;
    font-size: 0;
    @media ${device.lg} {
        margin-bottom: 80px;
    }
    &.last {
        margin-bottom: 0;
    }
`;
const DimmerItem = styled.div`
    height: 224px;
    width: 100%;
    &.item1 {
        background: linear-gradient(0deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0) 100%);
        margin-bottom: 32px;
        @media ${device.lg} {
            margin-bottom: 0;
        }
    }
    &.item2 {
        background: linear-gradient(90deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0) 100%);
        margin-bottom: 32px;
        @media ${device.lg} {
            margin-bottom: 0;
        }
    }
    &.item3 {
        background: linear-gradient(180deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0) 100%);
    }
    &.item4 {
        background: linear-gradient(-90deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0) 100%);
    }
`;


function DesignConstructor({ 
    intl,
    activeAnchor,
    setActiveAnchor,
    pageHeight,
    refs
    }) {

    const breakpoints = useBreakpoint();

    const goToId = (id) => {
        document.getElementById(id).scrollIntoView();
    }
        
    return (
        <ParentAnchor
            key='designConstructorSection'
            blockId="designConstructor-section"
            blockName='designConstructorSection'
            refs={refs}>
            <ChapterHeader title={intl.formatMessage({ id: 'designConstr_title' })}></ChapterHeader>
            <Container>
                <Grid>
                    <Cell col={12} colXl={10} colXxl={8} leftXl={2} leftXxl={3}>
                        <AnchorBlock
                            key='designComponents'
                            blockName='designComponents'
                            blockId='design-components'
                            activeAnchor={activeAnchor}
                            setActiveAnchor={setActiveAnchor}
                            pageHeight={pageHeight}
                            refs={refs}>
                                <H3 space="TopDown">{intl.formatMessage({ id: 'designConstr_1' })}</H3>
                                <Paragraph>{intl.formatMessage({ id: 'designConstr_subtitle' })}</Paragraph>
                                <Paragraph space="Down">
                                    {intl.formatMessage({ id: 'designConstr_1_1' })} <UnderlineLink onClick={() => { goToId('design-section'); }}>{intl.formatMessage({ id: 'designConstr_1_2' })}</UnderlineLink>:<br/>
                                    <AnchorLink onClick={() => { goToId('keyvisual-block'); }}>{intl.formatMessage({ id: 'designConstr_1_3' })}</AnchorLink><br/>
                                    <AnchorLink onClick={() => { goToId('co-branding-block'); }}>{intl.formatMessage({ id: 'designConstr_1_4' })}</AnchorLink><br/>
                                    <AnchorLink onClick={() => { goToId('value-block'); }}>{intl.formatMessage({ id: 'designConstr_1_5' })}</AnchorLink><br/>
                                    <AnchorLink onClick={() => { goToId('reason-block'); }}>{intl.formatMessage({ id: 'designConstr_1_6' })}</AnchorLink><br/>
                                    <AnchorLink onClick={() => { goToId('more-block'); }}>{intl.formatMessage({ id: 'designConstr_1_7' })}</AnchorLink><br/>
                                    <AnchorLink onClick={() => { goToId('call-to-action-block'); }}>{intl.formatMessage({ id: 'designConstr_1_8' })}</AnchorLink><br/>
                                    <AnchorLink onClick={() => { goToId('promo-badge-block'); }}>{intl.formatMessage({ id: 'designConstr_1_9' })}</AnchorLink>
                                </Paragraph>
                                <ConstructorImg>
                                    <ImageMultilang 
                                        intl={intl} 
                                        last="xs"
                                        imageRu='constructor/comp-exemple-ru@2x.png'
                                        imageUk='constructor/comp-exemple-uk@2x.png'
                                        imageEn='constructor/comp-exemple-en@2x.png'/>
                                </ConstructorImg>
                                <Paragraph space="Top">
                                    {intl.formatMessage({ id: 'designConstr_1_10' })}<br/>
                                    <AnchorLink onClick={() => { goToId('set-of-devices-block'); }}>{intl.formatMessage({ id: 'designConstr_1_11' })}</AnchorLink><br/>
                                    <AnchorLink onClick={() => { goToId('mini-set-block'); }}>{intl.formatMessage({ id: 'designConstr_1_12' })}</AnchorLink><br/>
                                    <AnchorLink onClick={() => { goToId('contentfield-block'); }}>{intl.formatMessage({ id: 'designConstr_1_14' })}</AnchorLink><br/>
                                    <AnchorLink onClick={() => { goToId('dimmer-block'); }}>{intl.formatMessage({ id: 'designConstr_1_13' })}</AnchorLink>
                                </Paragraph>

                                <div id="set-of-devices-block">
                                    <H4 space="TopDown">{intl.formatMessage({ id: 'designConstr_2_1' })}</H4>
                                    <Paragraph space="Down">{intl.formatMessage({ id: 'designConstr_2_2' })}</Paragraph>
                                    <Grid>
                                        <Cell col={6}>
                                            <SetImg>
                                                <PlainImage src="constructor/set-white1@2x.png" last="xs" alt="" />
                                            </SetImg>
                                        </Cell>
                                        <Cell col={6}>
                                            <SetImg>
                                                <PlainImage src="constructor/set-black1@2x.png" last="xs" alt="" />
                                            </SetImg>
                                        </Cell>
                                    </Grid>
                                    <Grid>
                                        <Cell col={6}>
                                            <SetImg>
                                                <PlainImage src="constructor/set-white2@2x.png" last="xs" alt="" />
                                            </SetImg>
                                        </Cell>
                                        <Cell col={6}>
                                            <SetImg>
                                                <PlainImage src="constructor/set-black2@2x.png" last="xs" alt="" />
                                            </SetImg>
                                        </Cell>
                                    </Grid>
                                    <Grid>
                                        <Cell col={6}>
                                            <SetImg className="last">
                                                <PlainImage src="constructor/set-white3@2x.png" last="xs" alt="" />
                                            </SetImg>
                                        </Cell>
                                        <Cell col={6}>
                                            <SetImg className="last">
                                                <PlainImage src="constructor/set-black3@2x.png" last="xs" alt="" />
                                            </SetImg>
                                        </Cell>
                                    </Grid>

                                    <H5 space="TopDown"><FormattedHTMLMessage id="designConstr_2_4" /></H5>
                                    <Grid>
                                        <Cell col={6}>
                                            <RuleDescription rule="do" />
                                            <ImageMultilang 
                                                intl={intl} 
                                                last="xs"
                                                imageRu='constructor/device-set-ex-1-ru@2x.jpg'
                                                imageUk='constructor/device-set-ex-1-uk@2x.jpg'
                                                imageEn='constructor/device-set-ex-1-en@2x.jpg'/>
                                        </Cell>
                                        <Cell col={6}>
                                            <RuleDescription rule="do" />
                                            <ImageMultilang 
                                                intl={intl} 
                                                last="xs"
                                                imageRu='constructor/device-set-ex-2-ru@2x.jpg'
                                                imageUk='constructor/device-set-ex-2-uk@2x.jpg'
                                                imageEn='constructor/device-set-ex-2-en@2x.jpg'/>
                                        </Cell>
                                    </Grid>
                                </div>
                                
                                <div id="mini-set-block">
                                    <H4 space="TopDown">{intl.formatMessage({ id: 'designConstr_3_1' })}</H4>
                                    <Paragraph space="Down">{intl.formatMessage({ id: 'designConstr_3_2' })}</Paragraph>
                                    <Grid>
                                        <Cell col={6}>
                                            <SetImg className="last">
                                                <PlainImage src="constructor/device-set-mini-1@2x.png" last="xs" alt="" />
                                            </SetImg>
                                        </Cell>
                                        <Cell col={6}>
                                            <SetImg className="last">
                                                <PlainImage src="constructor/device-set-mini-2@2x.png" last="xs" alt="" />
                                            </SetImg>
                                        </Cell>
                                    </Grid>

                                    <H5 space="TopDown"><FormattedHTMLMessage id="designConstr_3_4" /></H5>

                                    {breakpoints.sm ? 
                                        <>
                                            <Grid>
                                                <Cell col={6}>
                                                    <RuleDescription rule="do"><FormattedHTMLMessage id="designConstr_3_6" /></RuleDescription>
                                                </Cell>
                                                <Cell col={6}>
                                                    <RuleDescription rule="do"><FormattedHTMLMessage id="designConstr_3_5" /></RuleDescription>
                                                </Cell>
                                            </Grid>
                                            <Grid>
                                                <Cell col={6}>
                                                    <ImageMultilang 
                                                        intl={intl} 
                                                        last="sm"
                                                        imageRu='constructor/ex-3.1-ru@2x.jpg'
                                                        imageUk='constructor/ex-3.1-uk@2x.jpg'
                                                        imageEn='constructor/ex-3.1-en@2x.jpg'/>
                                                </Cell>
                                                <Cell col={6}>
                                                    <ImageMultilang 
                                                        intl={intl} 
                                                        last="xs"
                                                        imageRu='constructor/ex-3.2-ru@2x.jpg'
                                                        imageUk='constructor/ex-3.2-uk@2x.jpg'
                                                        imageEn='constructor/ex-3.2-en@2x.jpg'/>
                                                </Cell>
                                            </Grid>
                                        </>
                                    : 
                                        <Grid>
                                            <Cell col={12} colSm={6}>
                                                <RuleDescription rule="do">{intl.formatMessage({ id: 'designConstr_3_6' })}</RuleDescription>
                                                <ImageMultilang 
                                                    intl={intl} 
                                                    last="sm"
                                                    imageRu='constructor/ex-3.1-ru@2x.jpg'
                                                    imageUk='constructor/ex-3.1-uk@2x.jpg'
                                                    imageEn='constructor/ex-3.1-en@2x.jpg'/>
                                            </Cell>
                                            <Cell col={12} colSm={6}>
                                                <RuleDescription rule="do">{intl.formatMessage({ id: 'designConstr_3_5' })}</RuleDescription>
                                                <ImageMultilang 
                                                    intl={intl} 
                                                    last="xs"
                                                    imageRu='constructor/ex-3.2-ru@2x.jpg'
                                                    imageUk='constructor/ex-3.2-uk@2x.jpg'
                                                    imageEn='constructor/ex-3.2-en@2x.jpg'/>
                                            </Cell>
                                        </Grid>
                                    }
                                </div>
                                
                                <div id="contentfield-block">
                                    <H4 space="TopDown">{intl.formatMessage({ id: 'designConstr_4_1' })}</H4>
                                    <Paragraph>{intl.formatMessage({ id: 'designConstr_4_2' })}</Paragraph>

                                    <H5 space="TopDown"><FormattedHTMLMessage id="designConstr_4_3" /></H5>
                                    <RuleDescription rule="do" />
                                    <ImageMultilang 
                                        intl={intl} 
                                        // last="xs"
                                        imageRu='constructor/ex-4-ru@2x.jpg'
                                        imageUk='constructor/ex-4-uk@2x.jpg'
                                        imageEn='constructor/ex-4-en@2x.jpg'/>
                                    <ImageMultilang 
                                        intl={intl} 
                                        last="xs"
                                        imageRu='constructor/ex-4.2-ru@2x.jpg'
                                        imageUk='constructor/ex-4.2-uk@2x.jpg'
                                        imageEn='constructor/ex-4.2-en@2x.jpg'/>
                                </div>
                                
                                <div id="dimmer-block">
                                    <H4 space="TopDown">{intl.formatMessage({ id: 'designConstr_5_1' })}</H4>
                                    <Paragraph space="Down">{intl.formatMessage({ id: 'designConstr_5_2' })}</Paragraph>
                                    <Grid>
                                        <Cell col={6} colLg={3}>
                                            <DimmerItem className="item1"></DimmerItem>
                                        </Cell>
                                        <Cell col={6} colLg={3}>
                                            <DimmerItem className="item2"></DimmerItem>
                                        </Cell>
                                        <Cell col={6} colLg={3}>
                                            <DimmerItem className="item3"></DimmerItem>
                                        </Cell>
                                        <Cell col={6} colLg={3}>
                                            <DimmerItem className="item4"></DimmerItem>
                                        </Cell>
                                    </Grid>

                                    <H5 space="TopDown"><FormattedHTMLMessage id="designConstr_5_4" /></H5>
                                    {breakpoints.sm ? 
                                        <>
                                            <Grid>
                                                <Cell col={6}>
                                                    <RuleDescription>{intl.formatMessage({ id: 'designConstr_5_5' })}</RuleDescription>
                                                </Cell>
                                                <Cell col={6}>
                                                    <RuleDescription rule="do">{intl.formatMessage({ id: 'designConstr_5_6' })}</RuleDescription>
                                                </Cell>
                                            </Grid>
                                            <Grid>
                                                <Cell col={6}>
                                                    <ImageMultilang 
                                                        intl={intl} 
                                                        last="sm"
                                                        imageRu='constructor/ex-4.1-1-ru@2x.jpg'
                                                        imageUk='constructor/ex-4.1-1-uk@2x.jpg'
                                                        imageEn='constructor/ex-4.1-1-en@2x.jpg'/>
                                                </Cell>
                                                <Cell col={6}>
                                                    <ImageMultilang 
                                                        intl={intl} 
                                                        last="xs"
                                                        imageRu='constructor/ex-4.2-2-ru@2x.jpg'
                                                        imageUk='constructor/ex-4.2-2-uk@2x.jpg'
                                                        imageEn='constructor/ex-4.2-2-en@2x.jpg'/>
                                                </Cell>
                                            </Grid>
                                        </>
                                    : 
                                        <Grid>
                                            <Cell col={12} colSm={6}>
                                                <RuleDescription>{intl.formatMessage({ id: 'designConstr_5_5' })}</RuleDescription>
                                                <ImageMultilang 
                                                    intl={intl} 
                                                    last="sm"
                                                    imageRu='constructor/ex-4.1-1-ru@2x.jpg'
                                                    imageUk='constructor/ex-4.1-1-uk@2x.jpg'
                                                    imageEn='constructor/ex-4.1-1-en@2x.jpg'/>
                                            </Cell>
                                            <Cell col={12} colSm={6}>
                                                <RuleDescription rule="do">{intl.formatMessage({ id: 'designConstr_5_6' })}</RuleDescription>
                                                <ImageMultilang 
                                                    intl={intl} 
                                                    last="xs"
                                                    imageRu='constructor/ex-4.2-2-ru@2x.jpg'
                                                    imageUk='constructor/ex-4.2-2-uk@2x.jpg'
                                                    imageEn='constructor/ex-4.2-2-en@2x.jpg'/>
                                            </Cell>
                                        </Grid>
                                    }
                                </div>
                        </AnchorBlock>
                        <AnchorBlock
                            key='designLayouts'
                            blockName='designLayouts'
                            blockId='design-layouts'
                            activeAnchor={activeAnchor}
                            setActiveAnchor={setActiveAnchor}
                            pageHeight={pageHeight}
                            refs={refs}>
                            <H3 space="Top">{intl.formatMessage({ id: 'designConstr_6_1' })}</H3>
                            <H4 space="TopDown">{intl.formatMessage({ id: 'designConstr_6_2' })}</H4>
                            <Paragraph>{intl.formatMessage({ id: 'designConstr_6_3' })}</Paragraph>
                            <H5 space="TopDown">{intl.formatMessage({ id: 'designConstr_6_4' })}</H5>
                            <Paragraph space="Down"><FormattedHTMLMessage id="designConstr_6_5" /></Paragraph>
                            <Grid>
                                <Cell col={12} colSm={6}>
                                    <RuleDescription>{intl.formatMessage({ id: 'designConstr_6_6' })}</RuleDescription>
                                    {/* <PlainImage src="constructor/ex-5.0-en@2x.jpg" alt="" /> */}
                                    <ImageMultilang 
                                        intl={intl} 
                                        // last="xs"
                                        imageRu='constructor/ex-5.0-ru@2x.jpg'
                                        imageUk='constructor/ex-5.0-uk@2x.jpg'
                                        imageEn='constructor/ex-5.0-en@2x.jpg'/>
                                </Cell>
                            </Grid>
                            {breakpoints.sm ? 
                                <>
                                    <Grid>
                                        <Cell col={6}>
                                            <RuleDescription rule="do"><FormattedHTMLMessage id="designConstr_6_7" /></RuleDescription>
                                        </Cell>
                                        <Cell col={6}>
                                            <RuleDescription rule="do"><FormattedHTMLMessage id="designConstr_6_8" /></RuleDescription>
                                        </Cell>
                                    </Grid>
                                    <Grid>
                                        <Cell col={6}>
                                            <ImageMultilang 
                                                intl={intl} 
                                                last="sm"
                                                imageRu='constructor/ex-5.1-ru@2x.jpg'
                                                imageUk='constructor/ex-5.1-uk@2x.jpg'
                                                imageEn='constructor/ex-5.1-en@2x.jpg'/>
                                        </Cell>
                                        <Cell col={6}>
                                            <ImageMultilang 
                                                intl={intl} 
                                                last="xs"
                                                imageRu='constructor/ex-5.2-ru@2x.jpg'
                                                imageUk='constructor/ex-5.2-uk@2x.jpg'
                                                imageEn='constructor/ex-5.2-en@2x.jpg'/>
                                        </Cell>
                                    </Grid>
                                </>
                            : 
                                <Grid>
                                    <Cell col={12} colSm={6}>
                                        <RuleDescription rule="do">{intl.formatMessage({ id: 'designConstr_6_7' })}</RuleDescription>
                                        <ImageMultilang 
                                            intl={intl} 
                                            last="sm"
                                            imageRu='constructor/ex-5.1-ru@2x.jpg'
                                            imageUk='constructor/ex-5.1-uk@2x.jpg'
                                            imageEn='constructor/ex-5.1-en@2x.jpg'/>
                                    </Cell>
                                    <Cell col={12} colSm={6}>
                                        <RuleDescription rule="do">{intl.formatMessage({ id: 'designConstr_6_8' })}</RuleDescription>
                                        <ImageMultilang 
                                            intl={intl} 
                                            last="xs"
                                            imageRu='constructor/ex-5.2-ru@2x.jpg'
                                            imageUk='constructor/ex-5.2-uk@2x.jpg'
                                            imageEn='constructor/ex-5.2-en@2x.jpg'/>
                                    </Cell>
                                </Grid>
                            }

                            <H5 space="TopDown"><FormattedHTMLMessage id="designConstr_6_9" /></H5>
                            <Paragraph space="Down">{intl.formatMessage({ id: 'designConstr_6_10' })}</Paragraph>
                            <PlainImage src="constructor/grid@2x.jpg" last="xs" alt="" />

                            <H4 space="TopDown">{intl.formatMessage({ id: 'designConstr_7_1' })}</H4>
                            <Paragraph space="Down">{intl.formatMessage({ id: 'designConstr_7_2' })}</Paragraph>

                            {breakpoints.sm ? 
                                <>
                                    <Grid>
                                        <Cell col={6}>
                                            <RuleDescription>{intl.formatMessage({ id: 'designConstr_7_3' })}</RuleDescription>
                                        </Cell>
                                        <Cell col={6}>
                                            <RuleDescription rule="do"><FormattedHTMLMessage id="designConstr_7_4" /></RuleDescription>
                                        </Cell>
                                    </Grid>
                                    <Grid>
                                        <Cell col={6}>
                                            <ImageMultilang 
                                                intl={intl} 
                                                last="sm"
                                                imageRu='constructor/ex-6.1-ru@2x.jpg'
                                                imageUk='constructor/ex-6.1-uk@2x.jpg'
                                                imageEn='constructor/ex-6.1-en@2x.jpg'/>
                                        </Cell>
                                        <Cell col={6}>
                                            <ImageMultilang 
                                                intl={intl} 
                                                last="xs"
                                                imageRu='constructor/ex-6.2-ru@2x.jpg'
                                                imageUk='constructor/ex-6.2-uk@2x.jpg'
                                                imageEn='constructor/ex-6.2-en@2x.jpg'/>
                                        </Cell>
                                    </Grid>
                                </>
                            : 
                                <Grid>
                                    <Cell col={12} colSm={6}>
                                        <RuleDescription>{intl.formatMessage({ id: 'designConstr_7_3' })}</RuleDescription>
                                        <ImageMultilang 
                                            intl={intl} 
                                            last="sm"
                                            imageRu='constructor/ex-6.1-ru@2x.jpg'
                                            imageUk='constructor/ex-6.1-uk@2x.jpg'
                                            imageEn='constructor/ex-6.1-en@2x.jpg'/>
                                    </Cell>
                                    <Cell col={12} colSm={6}>
                                        <RuleDescription rule="do"><FormattedHTMLMessage id="designConstr_7_4" /></RuleDescription>
                                        <ImageMultilang 
                                            intl={intl} 
                                            last="xs"
                                            imageRu='constructor/ex-6.2-ru@2x.jpg'
                                            imageUk='constructor/ex-6.2-uk@2x.jpg'
                                            imageEn='constructor/ex-6.2-en@2x.jpg'/>
                                    </Cell>
                                </Grid>
                            }

                            <RuleDescription space="TopDown" rule="do"><FormattedHTMLMessage id="designConstr_7_5" /></RuleDescription>
                            <Grid>
                                <Cell col={12} colSm={6}>
                                    <ImageMultilang 
                                        intl={intl} 
                                        last="sm"
                                        imageRu='constructor/ex-6.3-ru@2x.jpg'
                                        imageUk='constructor/ex-6.3-uk@2x.jpg'
                                        imageEn='constructor/ex-6.3-en@2x.jpg'/>
                                </Cell>
                                <Cell col={12} colSm={6}>
                                    <ImageMultilang 
                                        intl={intl} 
                                        last="xs"
                                        imageRu='constructor/ex-6.4-ru@2x.jpg'
                                        imageUk='constructor/ex-6.4-uk@2x.jpg'
                                        imageEn='constructor/ex-6.4-en@2x.jpg'/>
                                </Cell>
                            </Grid>

                            <H4 space="TopDown">{intl.formatMessage({ id: 'designConstr_8_1' })}</H4>
                            <Paragraph space="Down">{intl.formatMessage({ id: 'designConstr_8_2' })}</Paragraph>
                            {breakpoints.sm ? 
                                <>
                                    <Grid>
                                        <Cell col={6}>
                                            <RuleDescription>{intl.formatMessage({ id: 'designConstr_8_3' })}</RuleDescription>
                                        </Cell>
                                        <Cell col={6}>
                                            <RuleDescription rule="do"><FormattedHTMLMessage id="designConstr_8_4" /></RuleDescription>
                                        </Cell>
                                    </Grid>
                                    <Grid>
                                        <Cell col={6}>
                                            <ImageMultilang 
                                                intl={intl} 
                                                imageRu='constructor/ex-7.1-ru@2x.jpg'
                                                imageUk='constructor/ex-7.1-uk@2x.jpg'
                                                imageEn='constructor/ex-7.1-en@2x.jpg'/>
                                        </Cell>
                                        <Cell col={6}>
                                            <ImageMultilang 
                                                intl={intl} 
                                                imageRu='constructor/ex-7.2-ru@2x.jpg'
                                                imageUk='constructor/ex-7.2-uk@2x.jpg'
                                                imageEn='constructor/ex-7.2-en@2x.jpg'/>
                                        </Cell>
                                    </Grid>
                                </>
                            : 
                                <Grid>
                                    <Cell col={12} colSm={6}>
                                        <RuleDescription>{intl.formatMessage({ id: 'designConstr_8_3' })}</RuleDescription>
                                        <ImageMultilang 
                                            intl={intl} 
                                            imageRu='constructor/ex-7.1-ru@2x.jpg'
                                            imageUk='constructor/ex-7.1-uk@2x.jpg'
                                            imageEn='constructor/ex-7.1-en@2x.jpg'/>
                                    </Cell>
                                    <Cell col={12} colSm={6}>
                                        <RuleDescription rule="do"><FormattedHTMLMessage id="designConstr_8_4" /></RuleDescription>
                                        <ImageMultilang 
                                            intl={intl} 
                                            imageRu='constructor/ex-7.2-ru@2x.jpg'
                                            imageUk='constructor/ex-7.2-uk@2x.jpg'
                                            imageEn='constructor/ex-7.2-en@2x.jpg'/>
                                    </Cell>
                                </Grid>
                            }

                            <RuleDescription>{intl.formatMessage({ id: 'designConstr_8_5' })}</RuleDescription>
                            <ImageMultilang 
                                intl={intl} 
                                imageRu='constructor/ex-8-ru@2x.jpg'
                                imageUk='constructor/ex-8-uk@2x.jpg'
                                imageEn='constructor/ex-8-en@2x.jpg'/>
                        </AnchorBlock>
                    </Cell>
                </Grid>
            </Container>
            <ChapterSpace />
        </ParentAnchor>
        
    )
}

export default injectIntl(DesignConstructor)


